import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { z } from "zod";
import { TFunction } from "i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { AlertCircle, CircleCheckBig, LoaderCircle } from "lucide-react";
import { ComponentProps, useEffect, useMemo, useState } from "react";

import {
  changePassword,
  ChangePasswordErrorType,
} from "@/api/authentication/changePassword.ts";
import { getErrorResponse } from "@/api/common/handleAxiosError.ts";
import { setTitle } from "@/hooks/useHeader.ts";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput.tsx";
import { useTranslation } from "@/i18n";

const createFormSchema = (t: TFunction) =>
  z
    .object({
      currentPassword: z.string().min(1),
      newPassword: z.string().min(8, t("validation.passwordMinLength")),
      confirmPassword: z.string().min(8, t("validation.passwordMinLength")),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t("validation.passwordsDoNotMatch"),
      path: ["confirmPassword"],
    });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const ChangePassword = ({ className, ...props }: ComponentProps<"div">) => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const formSchema = useMemo(() => createFormSchema(t), [t]);

  useEffect(() => {
    setTitle(t("changePassword.title"));
  }, [t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    setError("");
    try {
      await changePassword(values);
      toast(t("changePassword.success"), {
        duration: 3000,
        icon: <CircleCheckBig className="size-4 text-green-600" />,
      });
    } catch (e: unknown) {
      const response = getErrorResponse<ChangePasswordErrorType>(e);
      setError(
        response?.length ? response[0] : t("common.somethingWentWrongTryLater"),
      );
    }
    setLoading(false);
  };

  return (
    <div
      className={cn(
        "mx-auto flex max-w-md min-w-[45vw] flex-col gap-6",
        className,
      )}
      {...props}
    >
      <Card>
        <CardContent className="py-6">
          {error && (
            <Alert variant="destructive" className="mx-auto my-4 w-fit">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("changePassword.failed")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid gap-6">
                <div className="grid gap-6">
                  <FormField
                    control={form.control}
                    name="currentPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="currentPassword">
                          {t("form.currentPassword")}
                        </FormLabel>
                        <FormControl>
                          <PasswordInput
                            id="currentPassword"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setError(undefined);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="newPassword">
                          {t("form.newPassword")}
                        </FormLabel>
                        <FormControl>
                          <PasswordInput
                            id="newPassword"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setError(undefined);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel htmlFor="confirmPassword">
                          {t("form.confirmPassword")}
                        </FormLabel>
                        <FormControl>
                          <PasswordInput
                            id="confirmPassword"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setError(undefined);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={loading}>
                    {t("changePassword.submit")}{" "}
                    {loading && <LoaderCircle className="animate-spin" />}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePassword;
